import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

const COLORS = [
  '#1565c0', '#6a1b9a', '#c62828', '#2e7d32',
  '#e65100', '#00695c', '#4527a0', '#ad1457', '#0277bd', '#558b2f',
];

function hashWord(word) {
  let h = 0;
  for (let i = 0; i < word.length; i++) h = (h * 31 + word.charCodeAt(i)) & 0xffffff;
  return h;
}

/**
 * @param {Array<{word: string, displayWord?: string, amount: number}>} results
 * @param {Array<{raw: string, canonical: string}>} yourWords
 * @param {boolean} [blurOthers] – if true, non-user words are rendered as blurred placeholders
 */
export default function WordCloud({ results = [], yourWords = [], blurOthers = false }) {
  const { t } = useTranslation();
  if (!results.length) return null;

  const yourWordSet = new Set(yourWords.map((w) => w.canonical?.toLowerCase()).filter(Boolean));
  const maxAmount = Math.max(...results.map((r) => r.amount || 0), 1);
  const MIN_SIZE = 12;
  const MAX_SIZE = 42;

  // Sort biggest first so the layout leads with the most popular words
  const sorted = [...results].sort((a, b) => (b.amount || 0) - (a.amount || 0));

  return (
    <Box sx={{ my: 1 }}>
      <Typography
        variant="subtitle2"
        fontWeight={600}
        textAlign="center"
        color="text.secondary"
        gutterBottom
      >
        {t('results.wordCloudTitle')}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '6px 10px',
          px: 1,
          py: 1.5,
        }}
      >
        {sorted.map((row, idx) => {
          const amount = row.amount || 0;
          const ratio = maxAmount > 1 ? (amount - 1) / (maxAmount - 1) : 1;
          const fontSize = MIN_SIZE + ratio * (MAX_SIZE - MIN_SIZE);
          const isYours = yourWordSet.has(row.word?.toLowerCase());
          const displayText = row.displayWord ?? row.word;

          if (blurOthers && !isYours) {
            // Blurred placeholder proportional to word length and amount
            const width = Math.max(30, Math.min(90, (displayText?.length || 5) * (fontSize / 3)));
            return (
              <Box
                key={idx}
                sx={{
                  display: 'inline-block',
                  bgcolor: 'grey.400',
                  borderRadius: 1,
                  height: `${fontSize * 0.9}px`,
                  width: `${width}px`,
                  filter: 'blur(4px)',
                  opacity: 0.5 + ratio * 0.4,
                  flexShrink: 0,
                }}
              />
            );
          }

          const color = isYours
            ? '#1976d2'
            : COLORS[Math.abs(hashWord(displayText || '')) % COLORS.length];

          return (
            <Typography
              key={idx}
              component="span"
              title={`${displayText}: ${amount}`}
              sx={{
                fontSize: `${fontSize}px`,
                fontWeight: isYours ? 800 : ratio > 0.6 ? 700 : 500,
                color,
                lineHeight: 1.25,
                cursor: 'default',
                textTransform: 'capitalize',
                transition: 'transform 0.15s, opacity 0.15s',
                ...(isYours && {
                  textDecoration: 'underline',
                  textDecorationStyle: 'dotted',
                  textUnderlineOffset: '3px',
                }),
                '&:hover': { transform: 'scale(1.12)', opacity: 0.8 },
              }}
            >
              {displayText}
            </Typography>
          );
        })}
      </Box>
    </Box>
  );
}
